import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { EnterpriseProgramMemberships_ProgramMembershipState as MembershipState } from '__generated__/graphql-types';
import { flatMap } from 'lodash';

import type { SearchScope } from 'bundles/enterprise-learner-search/components/search/types';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import ProgramSwitcherQueryDocument from 'bundles/program-home/queries/ProgramSwitcherQuery.graphql';
import type {
  ProgramSwitcherQueryQuery as ProgramSwitcherQuery,
  ProgramSwitcherQueryQueryVariables as ProgramSwitcherQueryVariables,
} from 'bundles/program-home/queries/__generated__/ProgramSwitcherQuery';

import _t from 'i18n!nls/enterprise-learner-search';

export type UseUnifiedSearchBarPropsParams = {
  programSlug: string;
  programName: string;
  thirdPartyOrgSlug: string;
  thirdPartyOrganizationId: string;
  userId: number;
  programId: string;
  enableSkillsInSearchAndBrowse?: boolean;
};

type UnifiedSearchBarProps = {
  scopes: SearchScope[];
  initialScopeIndex: number;
  enableSkillsInSearchAndBrowse?: boolean;
  testId: string;
};

export const useUnifiedSearchBarProps = ({
  programSlug,
  programName,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
  userId,
  programId,
  enableSkillsInSearchAndBrowse,
}: UseUnifiedSearchBarPropsParams): UnifiedSearchBarProps => {
  const shouldSkip = !thirdPartyOrgSlug || !programSlug || !userId;

  const { data } = useQuery<ProgramSwitcherQuery, ProgramSwitcherQueryVariables>(ProgramSwitcherQueryDocument, {
    skip: shouldSkip,
    variables: shouldSkip ? undefined : { userId: Number(userId), includeInvitations: false },
    fetchPolicy: 'cache-only',
  });

  return useMemo(() => {
    const scopes: Array<SearchScope & { key: string }> = filterExistsOrDefault(
      data?.ProgramMemberships?.queryProgramMembershipsByUserIdAndIncludeInvitations?.edges
    )
      .map((edge) => edge.node)
      .filter(
        (node) =>
          node &&
          node.program != null &&
          node.program?.naptimeThirdPartyOrganization?.id === thirdPartyOrganizationId &&
          node.state === MembershipState.ProgramMembershipStateMember
      )
      .map((node) => {
        if (!node) return null;
        const {
          id: programId0,
          name: programName0,
          slug: programSlug0,
          // See above filter().
        } = node.program!;
        return {
          key: programSlug0,
          trackingData: { programSlug: programSlug0 },
          pathname: `/programs/${programSlug0}`,
          placeholder: _t("Search in '#{programName}'...", { programName: programName0 }),
          title: programName0,
          programIds: [programId0],
        };
      })
      .filter(Boolean);

    const programIds = flatMap(scopes, (scope) => scope.programIds);

    if (!scopes.find(({ key }) => key === programSlug)) {
      // They aren't a member of the program, but we should show it anyway
      scopes.push({
        key: programSlug,
        trackingData: { programSlug },
        pathname: `/programs/${programSlug}`,
        placeholder: _t("Search in '#{programName}'...", { programName }),
        title: programName,
        programIds: [programId],
      });
    }

    scopes.sort((a, b) => a.placeholder.localeCompare(b.placeholder));

    if (userId && scopes.length > 1) {
      scopes.unshift({
        key: thirdPartyOrgSlug,
        trackingData: { thirdPartyOrgSlug },
        pathname: `/programs/all/${thirdPartyOrgSlug}`,
        placeholder: _t('Search in all programs you joined...'),
        title: _t('All Learning Programs'),
        programIds,
      });
    }

    const initialScopeIndex = Math.max(
      scopes.findIndex(({ key }) => key === programSlug),
      0
    );

    return { scopes, initialScopeIndex, enableSkillsInSearchAndBrowse, testId: 'unified-enterprise-search-bar' };
  }, [
    data,
    programSlug,
    programName,
    thirdPartyOrgSlug,
    thirdPartyOrganizationId,
    userId,
    programId,
    enableSkillsInSearchAndBrowse,
  ]);
};
